/**
 * Keep a quality picked by hand until it is the one on screen.
 *
 * A pick takes over at the next segment (see settleHls). Until it does,
 * the segment already queued plays at the old quality, and hls.js
 * announces that as the level now playing. Vidstack treats every such
 * announcement as a choice: it marks that quality selected, and since
 * Auto is off, sets it as the level wanted. The pick is undone a few
 * milliseconds after it is made. Measured on a film stepped down by
 * hand: 288p picked, the queued 480p segment started, and 480p was
 * asked for again at once - so a pick seemed to go up one by itself,
 * or needed clicking twice.
 *
 * So the pick is remembered, and when an announcement selects
 * something else before the pick has reached the screen, the pick is
 * put back. Only announcements are undone, never a choice: a pick from
 * the menu sets the quality's `selected` property and its change event
 * carries no trigger, while an announcement's carries the hls.js event
 * it came from. Once hls.js announces the picked level itself, it is on
 * screen and there is nothing more to keep. That is read from hls.js,
 * not from the list: by then the list already has the pick selected,
 * and Vidstack sends nothing for a selection that changes nothing.
 *
 * Bounded, so it can never fight hls.js for long: if the picked level
 * cannot be played - it fails to load, say, and hls.js moves away from
 * it - it is put back at most RESTORES_PER_PICK times and then left.
 */

// What Vidstack names the trigger of a selection it made because
// hls.js announced a level as playing.
export const ANNOUNCED = 'hls-level-switched';

export const RESTORES_PER_PICK = 3;

type Quality = { selected: boolean; height?: number };
type Qualities = EventTarget & {
  auto: boolean;
  selected: Quality | null;
  // In the order of hls.levels, which is how Vidstack builds it.
  [index: number]: Quality | undefined;
};

export const keepThePick = (
  qualities: Qualities,
  hls: any,
  note?: (text: string) => void,
) => {
  let pick: Quality | null = null;
  let restores = 0;

  const onChange = (event: Event) => {
    const current =
      (event as CustomEvent).detail?.current ?? qualities.selected;
    const announced = (event as any).trigger?.type === ANNOUNCED;
    if (qualities.auto) {
      pick = null;
      return;
    }
    if (!announced) {
      // A pick from the menu - or this putting one back, which is the
      // same pick again.
      if (current !== pick) {
        pick = current;
        restores = 0;
      }
      return;
    }
    if (!pick || current === pick) {
      return;
    }
    if (restores >= RESTORES_PER_PICK) {
      note?.(`stopped holding ${pick.height ?? '?'}p: it never came on`);
      pick = null;
      return;
    }
    restores += 1;
    note?.(
      `kept the pick of ${pick.height ?? '?'}p over ${current?.height ?? '?'}p still playing`,
    );
    pick.selected = true;
  };

  // The level on screen now, as hls.js says it.
  const onSwitched = (_event: string, data: any) => {
    if (pick && qualities[data?.level] === pick) {
      pick = null;
    }
  };

  const onAutoChange = (event: Event) => {
    if ((event as CustomEvent).detail === true) {
      pick = null;
    }
  };

  qualities.addEventListener('change', onChange);
  qualities.addEventListener('auto-change', onAutoChange);
  hls.on('hlsLevelSwitched', onSwitched);
  return () => {
    qualities.removeEventListener('change', onChange);
    qualities.removeEventListener('auto-change', onAutoChange);
    hls.off('hlsLevelSwitched', onSwitched);
  };
};
